
/*!
 * Copyright (C) 2023-2024, Peppermill Inc.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *//*
 * @version 1.1.0
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @changelog
 *		2024-05-14 (1.0.0)
 *			- Add role="dialog" to <peppermill-dialog> container.
 *			- Give linkEle prop a type in <peppermill-dialog> container.
 *			- Give lastActiveElement data a type in <peppermill-dialog> container.
 *			- Remove "outline=none" CSS property values in <peppermill-dialog> container.
 *			- Add "cancel" emits in <peppermill-dialog> container.
 *
 *		2024-06-17 (1.1.0) (jhoward)
 *			- Add support for Pepvalidate V3 forms to <peppermill-dialog>.
 */

import {ComponentPublicInstance, defineComponent, PropType} from "vue";

export interface PeppermillDialogComponent extends ComponentPublicInstance {
  getDialogFormData: () => FormData | null;
}
export default defineComponent({
  name: "peppermill-dialog",
  emits: {
    close: () => true,
    cancel: () => true,
    submit: (formData: FormData | null) => true,
  },
  props: {
    title: {
      type: String,
      default: ''
    },
    labelConfirm: {
      type: String,
      default: 'OK'
    },
    labelDeny: {
      type: String,
      default: 'CANCEL'
    },
    isAlert: {
      type: Boolean,
      default: false
    },
    active: {
      type: Boolean,
      default: false
    },
    linkEle: {
      type: HTMLElement as PropType<HTMLElement | null>,
      default: () => null
    },
    wide: {
      type: Boolean,
      default: false
    },
    hideOverlay: {
      type: Boolean,
      default: false
    }
  },
  mounted: function () {
    $(() => {
      if (window.pepMaterial && window.pepMaterial.globalInitialize) {
        window.pepMaterial.globalInitialize.call(this.$el);
      } else if (window.pepvalidateInitForm && this.$el.querySelector('form.pepvalidate-v3')) {
        window.pepvalidateInitForm.call(this.$el.querySelector('form.pepvalidate-v3') as HTMLFormElement);
      }
    });

    window.addEventListener('resize', () => {
      this.windowHeight = window.innerHeight;
    }, {passive: true});
    window.addEventListener('load', () => {
      this.windowHeight = window.innerHeight;
    }, {passive: true});
  },
  data: function () {
    return {
      // lastActiveElement: null as HTMLElement | null,
      opening: false,
      closing: false,
      windowHeight: window.innerHeight,
      // windowScrollY: 0,
    };
  },
  computed: {
    bodyMaxHeight() {
      this.active;
      let maxHeight = this.windowHeight;
      if (this.$refs.dialog) {
        maxHeight -= parseInt(getComputedStyle(this.$refs.dialog as HTMLDivElement).paddingTop, 10);
      }
      if (this.$refs.title) {
        maxHeight -= (this.$refs.title as HTMLHeadingElement).clientHeight;
      }

      if (this.$refs.actions) {
        maxHeight -= (this.$refs.actions as HTMLDivElement).clientHeight;
      }
      return maxHeight + 'px';
    },
    containerClasses() {
      return {
        'dialog--container-active': this.active,
        'dialog--container-opening': this.opening,
        'dialog--container-closing': this.closing,
        'dialog--container-wide': this.wide,
      };
    },
    overlayClasses() {
      return {
        'dialog--overlay-show': !this.hideOverlay && this.active
      };
    },
  },
  watch: {
    active: function () {
      if (this.active) {
        //opening
        // this.lastActiveElement = document.activeElement as HTMLElement | null;

        setTimeout(() => {
          (this.$refs.dialog as HTMLDivElement).focus();
        }, 512);

      } else if (this.linkEle) {
        //closing
        if (!this.closing && !this.opening) {
          //re-focus last focused element before opening dialog
            this.linkEle.focus();
        }
      }
    }
  },
  methods: {
    handleContainerTransitionEnd: function (event: TransitionEvent) {
      if ('' === event.pseudoElement && 'opacity' === event.propertyName) {
        if (this.$el.classList.contains('dialog--container-opening')) {
          this.$el.classList.remove('dialog--container-opening');
        } else if (this.$el.classList.contains('dialog--container-closing')) {
          this.$el.classList.remove('dialog--container-closing');
        }
      }
    },

    getDialogFormData(): FormData | null {
      const formNode = this.$el.querySelector('form');
      if (formNode) {
        return new FormData(formNode);
      }

      return null;
    }
  }
})
